import React from 'react';
import PropTypes from 'prop-types';
import { alpha, useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import FavoriteIcon from '@material-ui/icons/Favorite';
import VisibilityIcon from '@material-ui/icons/Visibility';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import LightbulbIcon from '@material-ui/icons/EmojiObjects';
import PublicIcon from '@material-ui/icons/Public';
import DevicesIcon from '@material-ui/icons/Devices';

const DEFAULT_TITLE = 'Nuestros Principios';
const DEFAULT_DESCRIPTION =
  'Creemos en la conexión como el motor de experiencias inolvidables. Transformamos cada interacción en una oportunidad para explorar e innovar. Nuestra comunidad es el espacio donde la tecnología y la pasión se encuentran para redefinir el consumo de medios desde cualquier rincón del mundo.';

const DEFAULT_HIGHLIGHT = 'Creemos en la conexión como el motor de experiencias inolvidables.';

const MISSION = {
  label: 'Misión',
  text: 'Generar experiencias de conexión disruptivas para nuestro público y clientes a nivel global a través de una plataforma digital de vanguardia que integra audio, video y texto.',
  accent: '#1565c0',
  Icon: FavoriteIcon,
};

const VISION = {
  label: 'Visión',
  text: 'Reinventar constantemente la forma en que el mundo consume y produce contenido para liderar el mercado audiovisual y editorial multiplataforma.',
  accent: '#00838f',
  Icon: VisibilityIcon,
};

const PRINCIPLES = [
  {
    title: 'Conexión con propósito',
    text: 'Cada interacción en antena, On-Demand o Digest es una oportunidad de generar valor real para la audiencia y las marcas.',
    accent: '#c2185b',
    Icon: DeviceHubIcon,
  },
  {
    title: 'Innovación constante',
    text: 'Exploramos formatos, narrativas y herramientas que mantienen a TNO a la vanguardia del consumo de medios.',
    accent: '#6a1b9a',
    Icon: LightbulbIcon,
  },
  {
    title: 'Comunidad global',
    text: 'Un ecosistema donde la pasión por contar historias une voces, productores y audiencias en cualquier rincón del mundo.',
    accent: '#2e7d32',
    Icon: PublicIcon,
  },
  {
    title: 'Ecosistema 360°',
    text: 'Live streaming, archivo On-Demand y autoridad editorial en un solo lugar para amplificar el mensaje de cada aliado.',
    accent: '#e65100',
    Icon: DevicesIcon,
  },
];

function splitHighlight(description) {
  const text = String(description || '').trim();
  if (!text) return { highlight: DEFAULT_HIGHLIGHT, body: '' };
  const match = text.match(/^(.+?[.!?])(?:\s+([\s\S]+))?$/);
  if (!match) return { highlight: text, body: '' };
  return {
    highlight: match[1].trim(),
    body: (match[2] || '').trim(),
  };
}

function ValueCard({ children, accent, sx = {} }) {
  const theme = useTheme();
  const accentColor = accent || theme.palette.primary.main;
  return (
    <Box
      sx={{
        height: '100%',
        borderRadius: 3,
        p: { xs: 2, md: 2.5 },
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${alpha(accentColor, 0.2)}`,
        boxShadow: `0 10px 28px ${alpha(accentColor, 0.1)}`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

ValueCard.propTypes = {
  children: PropTypes.node,
  accent: PropTypes.string,
  sx: PropTypes.object,
};

function PillarCard({ item, variant = 'principle' }) {
  const { label, title, text, accent, Icon } = item;
  const heading = label || title;
  const isPillar = variant === 'pillar';

  return (
    <ValueCard
      accent={accent}
      sx={{
        borderLeft: `4px solid ${accent}`,
        ...(isPillar && {
          background: `linear-gradient(145deg, ${alpha(accent, 0.06)} 0%, transparent 55%)`,
        }),
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.5,
          bgcolor: alpha(accent, 0.12),
          color: accent,
        }}
      >
        <Icon sx={{ fontSize: 26 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: isPillar ? { xs: '0.72rem', md: '0.78rem' } : '0.95rem',
          letterSpacing: isPillar ? 2 : 0.5,
          textTransform: 'uppercase',
          color: accent,
          mb: 1,
        }}
      >
        {heading}
      </Typography>
      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: isPillar ? { xs: '0.95rem', md: '1.02rem' } : '0.9rem',
          lineHeight: 1.6,
        }}
      >
        {text}
      </Typography>
    </ValueCard>
  );
}

PillarCard.propTypes = {
  item: PropTypes.shape({
    label: PropTypes.string,
    title: PropTypes.string,
    text: PropTypes.string.isRequired,
    accent: PropTypes.string.isRequired,
    Icon: PropTypes.elementType.isRequired,
  }).isRequired,
  variant: PropTypes.oneOf(['pillar', 'principle']),
};

const Values = ({ content = {} }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#00838f';
  const title = content.title || DEFAULT_TITLE;
  const description = content.description || DEFAULT_DESCRIPTION;
  const { highlight, body } = splitHighlight(description);

  return (
    <Box
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        background: `linear-gradient(165deg, ${alpha(secondary, 0.07)} 0%, ${alpha(primary, 0.06)} 45%, transparent 100%)`,
        px: { xs: 1, md: 2 },
        py: { xs: 3, md: 5 },
        my: { xs: 2, md: 3 },
      }}
    >
      <Box textAlign="center" mb={{ xs: 3, md: 4 }} maxWidth={760} mx="auto">
        <Typography
          sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 2.5, fontSize: '0.78rem' }}
          color="secondary"
          gutterBottom
        >
          Valores
        </Typography>
        <Typography
          component="h2"
          sx={{
            fontWeight: 900,
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            letterSpacing: '-0.02em',
            color: 'primary.main',
            mb: 2,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.05rem', md: '1.2rem' },
            lineHeight: 1.55,
            color: 'text.primary',
            mb: body ? 1.5 : 0,
          }}
        >
          {highlight}
        </Typography>
        {body ? (
          <Typography sx={{ color: 'text.secondary', fontSize: '1rem', lineHeight: 1.7 }}>
            {body}
          </Typography>
        ) : null}
      </Box>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <PillarCard item={MISSION} variant="pillar" />
        </Grid>
        <Grid item xs={12} md={6}>
          <PillarCard item={VISION} variant="pillar" />
        </Grid>
      </Grid>

      <Typography
        sx={{
          textAlign: 'center',
          fontWeight: 800,
          fontSize: '0.8rem',
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: alpha(primary, 0.85),
          mb: 2,
        }}
      >
        Lo que nos guía cada día
      </Typography>

      <Grid container spacing={2}>
        {PRINCIPLES.map((principle) => (
          <Grid item xs={12} sm={6} key={principle.title}>
            <PillarCard item={principle} variant="principle" />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

Values.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }),
};

Values.defaultProps = {
  content: {},
};

export default Values;
